import React, { useRef, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";
import SlashCommandMenu, { filterCommands } from "./SlashCommandMenu";

/**
 * CaptureRow — the one primary action surface on /me.
 *
 * Desktop: pill capture bar + "+ New task" + "+ New brief" buttons in one row.
 * Mobile:  capture bar on top, two buttons stacked in a row below it.
 *
 * Slash commands (fires slash menu above the input):
 *   /task <text>      → /tasks/new?title=<text>   (full form)
 *   /brief <text>     → /bizdev/briefs/new?title=<text>   (full form)
 *   /priority <text>  → opens priorities modal (if onPromoteToPriority given)
 *                       else navigates to /me (priority modal auto-opens)
 *   /jot <text>       → creates a jot (same as plain-text + Enter)
 *
 * Plain text Enter → creates a jot via onJotCreate(text).
 * If no onJotCreate supplied, falls back to /tasks/new?title=<text> (Phase A default).
 */
export default function CaptureRow({ autoFocus = true, onJotCreate }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const inputRef = useRef(null);
  const [value, setValue] = React.useState("");
  const [slashOpen, setSlashOpen] = React.useState(false);
  const [slashSelected, setSlashSelected] = React.useState(0);

  useEffect(() => {
    if (autoFocus && !isMobile && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [autoFocus, isMobile]);

  // Slash detection — show menu only when input starts with "/"
  const rawSlash = value.startsWith("/");
  const slashQuery = rawSlash ? value.slice(1).split(/\s/)[0] || "" : "";
  const filteredCommands = React.useMemo(() => filterCommands(slashQuery), [slashQuery]);

  useEffect(() => {
    if (rawSlash && filteredCommands.length > 0) {
      setSlashOpen(true);
      // Clamp selected
      setSlashSelected((i) => Math.min(i, filteredCommands.length - 1));
    } else {
      setSlashOpen(false);
      setSlashSelected(0);
    }
  }, [rawSlash, filteredCommands.length]);

  const runCommand = useCallback(async (cmdId, freeText) => {
    const text = (freeText || "").trim();
    setValue("");
    setSlashOpen(false);
    if (cmdId === "task") {
      const q = text ? `?title=${encodeURIComponent(text)}` : "";
      navigate(`/tasks/new${q}`);
      return;
    }
    if (cmdId === "brief") {
      const q = text ? `?title=${encodeURIComponent(text)}` : "";
      navigate(`/bizdev/briefs/new${q}`);
      return;
    }
    if (cmdId === "priority") {
      // No silent create here — the DailyPriorityModal is the enforced path.
      // Navigate to /me (already on it) and trigger the modal by clearing today.
      // Simpler: dispatch a custom event the modal can listen for, or just show a hint.
      // For now, navigate to /me and let the user pick a slot manually via the modal.
      navigate("/me");
      // If text was provided, attempt to pre-fill via sessionStorage (picked up by modal, best-effort).
      if (text && typeof window !== "undefined") {
        try { window.sessionStorage.setItem("dm_priority_prefill", text); } catch (_) {}
      }
      return;
    }
    // "jot" or default
    if (onJotCreate && text) {
      try { await onJotCreate(text); } catch (err) { console.error("jot create failed", err); }
    } else if (text) {
      // Fallback for Phase A environments
      navigate(`/tasks/new?title=${encodeURIComponent(text)}`);
    }
  }, [navigate, onJotCreate]);

  const onKeyDown = async (e) => {
    // Slash menu keyboard handling
    if (slashOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSlashSelected((i) => (i + 1) % filteredCommands.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSlashSelected((i) => (i - 1 + filteredCommands.length) % filteredCommands.length);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setValue("");
        setSlashOpen(false);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const v = value.trim();
        const chosen = filteredCommands[slashSelected];
        if (!chosen) return;
        // Extract free text after "/cmd ":
        const afterSpace = v.indexOf(" ");
        const freeText = afterSpace > -1 ? v.slice(afterSpace + 1).trim() : "";
        runCommand(chosen.id, freeText);
        return;
      }
    }

    // Normal Enter (no slash menu)
    if (e.key !== "Enter") return;
    const v = value.trim();
    if (!v) return;
    e.preventDefault();

    // Explicit slash prefix without menu still honored
    if (v.startsWith("/task ")) {
      runCommand("task", v.slice(6).trim());
      return;
    }
    if (v.startsWith("/brief ")) {
      runCommand("brief", v.slice(7).trim());
      return;
    }
    if (v.startsWith("/priority ")) {
      runCommand("priority", v.slice(10).trim());
      return;
    }
    if (v.startsWith("/jot ")) {
      runCommand("jot", v.slice(5).trim());
      return;
    }

    // Plain text → jot
    if (onJotCreate) {
      setValue("");
      try { await onJotCreate(v); } catch (err) { console.error("Create jot failed:", err); }
      return;
    }
    navigate(`/tasks/new?title=${encodeURIComponent(v)}`);
    setValue("");
  };

  const pillBase = {
    height: 52,
    padding: "0 20px",
    background: "var(--s1)",
    border: "1px solid var(--bdr)",
    borderRadius: 999,
    color: "var(--t2)",
    fontFamily: "var(--font-sans)",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    transition: "all .15s",
    letterSpacing: "-0.005em",
    whiteSpace: "nowrap",
    flexShrink: 0,
  };

  const btnHover = (e, enter) => {
    if (enter) {
      e.currentTarget.style.background = "var(--s2)";
      e.currentTarget.style.borderColor = "var(--bdrh)";
      e.currentTarget.style.color = "var(--t1)";
      e.currentTarget.style.transform = "translateY(-1px)";
    } else {
      e.currentTarget.style.background = "var(--s1)";
      e.currentTarget.style.borderColor = "var(--bdr)";
      e.currentTarget.style.color = "var(--t2)";
      e.currentTarget.style.transform = "";
    }
  };

  const captureInput = (
    <div style={{ position: "relative", flex: 1 }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          // Close slash menu after blur (delayed so click-to-select registers first)
          setTimeout(() => setSlashOpen(false), 150);
          e.currentTarget.style.borderColor = "var(--bdr)";
          e.currentTarget.style.boxShadow = "";
          e.currentTarget.style.background = "var(--s1)";
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "#60a5fa";
          e.currentTarget.style.boxShadow = "0 0 0 4px rgba(96,165,250,.12)";
          e.currentTarget.style.background = "var(--bg)";
        }}
        placeholder={isMobile ? "Jot a thought…" : "What's on your mind? / for commands"}
        autoComplete="off"
        style={{
          width: "100%",
          height: isMobile ? 48 : 52,
          border: "1px solid var(--bdr)",
          background: "var(--s1)",
          borderRadius: 999,
          padding: isMobile ? "0 18px" : "0 22px",
          color: "var(--t1)",
          fontFamily: "var(--font-sans)",
          fontSize: isMobile ? 15 : 16,
          outline: "none",
          transition: "all .2s cubic-bezier(.4,0,.2,1)",
          letterSpacing: "-0.005em",
        }}
        className="dm-capture-input"
      />
      <SlashCommandMenu
        open={slashOpen}
        query={slashQuery}
        selectedIdx={slashSelected}
        onSelect={(cmdId) => {
          const v = value.trim();
          const afterSpace = v.indexOf(" ");
          const freeText = afterSpace > -1 ? v.slice(afterSpace + 1).trim() : "";
          runCommand(cmdId, freeText);
        }}
        onHover={(i) => setSlashSelected(i)}
      />
    </div>
  );

  const newTaskBtn = (
    <button
      type="button"
      onClick={() => navigate("/tasks/new")}
      style={pillBase}
      className="dm-capture-btn"
      onMouseEnter={(e) => btnHover(e, true)}
      onMouseLeave={(e) => btnHover(e, false)}
      title="Create a new task with required fields"
    >
      <Plus size={16} style={{ color: "#a78bfa" }} />
      <span>New task</span>
    </button>
  );

  const newBriefBtn = (
    <button
      type="button"
      onClick={() => navigate("/bizdev/briefs/new")}
      style={pillBase}
      className="dm-capture-btn"
      onMouseEnter={(e) => btnHover(e, true)}
      onMouseLeave={(e) => btnHover(e, false)}
      title="Create a new brief with required fields"
    >
      <Plus size={16} style={{ color: "#60a5fa" }} />
      <span>New brief</span>
    </button>
  );

  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
        {captureInput}
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            {React.cloneElement(newTaskBtn, {
              style: { ...pillBase, height: 44, fontSize: 13, width: "100%", justifyContent: "center" },
            })}
          </div>
          <div style={{ flex: 1 }}>
            {React.cloneElement(newBriefBtn, {
              style: { ...pillBase, height: 44, fontSize: 13, width: "100%", justifyContent: "center" },
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 32,
      }}
    >
      {captureInput}
      {newTaskBtn}
      {newBriefBtn}
    </div>
  );
}
